// Design language F building blocks for the home page and voice grids:
// voice card, collection card, collab card and a section heading. Kept
// presentational - data shapes are the /api/home/* responses.
import React from "react";
import Link from "next/link";
import millify from "millify";
import { Mic } from "lucide-react";
import { useT } from "@jammable/ui-core/src/i18n";
import VoiceImageJs from "../VoiceImage";

/* eslint-disable @typescript-eslint/no-explicit-any */

// VoiceImage is untyped JS whose props all read as required; every one is
// optional in practice.
const VoiceImage = VoiceImageJs as unknown as React.FC<any>;

export const SectionHeading = ({
  title,
  icon,
  iconBg = "tw-bg-jam-lav",
  aside,
}: {
  title: string;
  icon?: React.ReactNode;
  iconBg?: string;
  aside?: React.ReactNode;
}) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
    <div className="tw-flex tw-items-center tw-gap-3">
      {icon && (
        <span
          className={`tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full ${iconBg}`}
        >
          {icon}
        </span>
      )}
      <h2 className="jam-display tw-m-0 tw-text-[26px] tw-font-extrabold tw-text-jam-ink md:tw-text-[32px]">
        {title}
      </h2>
    </div>
    {aside}
  </div>
);

// The voice card used across the site (home, /models, hubs, voice pages,
// profiles, collections). Image-led: a square tile tinted with the image's
// own palette while it loads, the name, its creator and use count below. The whole
// card links to the voice page; the lime mic on hover hints at "use it".
export const JamVoiceCard = ({
  model,
  rank,
}: {
  model: any;
  rank?: number;
}) => {
  const rgb = model?.image?.colorPalette?.LightMuted?.rgb;
  const tint =
    Array.isArray(rgb) && rgb.length === 3 && rgb.some((c: number) => c > 0)
      ? `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`
      : undefined;
  const t = useT();
  return (
    <Link
      href={`/${model?.urlParam}`}
      className="tw-group tw-flex tw-min-w-0 tw-flex-col tw-gap-2 tw-text-jam-ink tw-no-underline"
    >
      <div
        className="tw-relative tw-aspect-square tw-w-full tw-overflow-hidden tw-rounded-[22px] tw-bg-jam-lav md:tw-rounded-[26px]"
        style={tint ? { backgroundColor: tint } : undefined}
      >
        <VoiceImage
          model={model}
          fill
          sizes="(max-width: 640px) 33vw, (max-width: 768px) 25vw, 200px"
          className="tw-object-cover tw-transition-transform tw-duration-300 tw-ease-out group-hover:tw-scale-[1.06]"
        />
        {rank ? (
          <span className="tw-absolute tw-left-2 tw-top-2 tw-flex tw-h-7 tw-min-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-px-2 tw-text-xs tw-font-extrabold tw-text-jam-ink md:tw-left-2.5 md:tw-top-2.5">
            {rank}
          </span>
        ) : null}
        <span className="tw-absolute tw-bottom-2 tw-right-2 tw-hidden tw-h-10 tw-w-10 tw-translate-y-1 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink tw-opacity-0 tw-transition-all tw-duration-200 group-hover:tw-translate-y-0 group-hover:tw-opacity-100 md:tw-flex">
          <Mic size={18} strokeWidth={2.5} />
        </span>
      </div>
      <div className="tw-min-w-0 tw-px-0.5">
        <div className="tw-truncate tw-text-[14px] tw-font-extrabold tw-leading-tight group-hover:tw-text-jam-purple md:tw-text-[15px]">
          {model?.artistname}
        </div>
        {/* Who made it, then its uses; the name gives way first. */}
        <div className="tw-mt-0.5 tw-flex tw-min-w-0 tw-text-xs tw-font-bold tw-text-jam-muted">
          {model?.user?.username && (
            <span className="tw-min-w-0 tw-truncate">
              @{model.user.username}
              <span className="tw-px-1">·</span>
            </span>
          )}
          <span className="tw-flex-shrink-0">
            {t("home.card.uses", { n: millify(model?.uses || 0) })}
          </span>
        </div>
      </div>
    </Link>
  );
};

export const JamCollectionCard = ({ collection }: { collection: any }) => {
  const t = useT();
  return (
    <Link
      href={`/collection/${collection._id}`}
      className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2.5 tw-text-jam-ink tw-no-underline"
    >
      <div className="tw-relative tw-h-[150px] tw-overflow-hidden tw-rounded-[28px] tw-bg-jam-lav md:tw-h-[170px]">
        {collection?.image?.url && (
          <VoiceImage
            model={collection}
            fill
            sizes="(max-width: 768px) 50vw, 240px"
            className="tw-object-cover"
          />
        )}
        <span className="tw-absolute tw-left-3 tw-top-3 tw-rounded-full tw-bg-white tw-px-2.5 tw-py-1 tw-text-xs tw-font-extrabold">
          {t("home.card.voices", { n: collection.modelsCount ?? "" })}
        </span>
      </div>
      <div className="tw-px-1">
        <div className="tw-line-clamp-1 tw-text-[15px] tw-font-extrabold">
          {collection.title}
        </div>
        <div className="tw-text-xs tw-font-bold tw-text-jam-muted">
          {t("home.card.likes", { n: millify(collection.likes || 0) })}
          {collection?.user?.username
            ? ` · ${t("home.card.by", { user: collection.user.username })}`
            : ""}
        </div>
      </div>
    </Link>
  );
};

export const JamComboCard = ({ combo }: { combo: any }) => {
  const t = useT();
  const names = (combo?.models || []).map((m: any) => m.artistname);
  return (
    <Link
      href={{
        pathname: "/create",
        query: { voices: combo.models.map((m: any) => m._id).join(",") },
      }}
      className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3 tw-rounded-[28px] tw-bg-jam-lav tw-p-[18px] tw-text-jam-ink tw-no-underline"
    >
      <div className="tw-flex">
        {combo?.models?.slice(0, 3).map((model: any, i: number) => (
          <div
            key={model._id}
            style={{ marginLeft: i ? -14 : 0, zIndex: 10 - i }}
            className="tw-relative tw-h-14 tw-w-14 tw-overflow-hidden tw-rounded-full tw-border-4 tw-border-solid tw-border-jam-lav"
          >
            <VoiceImage
              model={model}
              fill
              sizes="56px"
              className="tw-object-cover"
            />
          </div>
        ))}
      </div>
      <div>
        <div className="tw-line-clamp-1 tw-text-[15px] tw-font-extrabold">
          {names.join(" × ")}
        </div>
        <div className="tw-text-xs tw-font-bold tw-text-jam-muted">
          {t("home.card.madeToday", { n: combo.count ?? "" })}
        </div>
      </div>
      <span className="tw-self-start tw-rounded-full tw-bg-white tw-px-3.5 tw-py-2 tw-text-[13px] tw-font-extrabold tw-text-jam-purple">
        {t("home.card.makeDuet")}
      </span>
    </Link>
  );
};

// Same footprint as JamVoiceCard (square tile + two lines), so a grid
// doesn't jump when the cards arrive.
export const JamVoiceCardSkeleton = () => (
  <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2">
    <div className="tw-aspect-square tw-w-full tw-animate-pulse tw-rounded-[22px] tw-bg-jam-lav md:tw-rounded-[26px]" />
    <div className="tw-h-3.5 tw-w-3/4 tw-animate-pulse tw-rounded-full tw-bg-jam-lav" />
    <div className="tw-h-3 tw-w-1/2 tw-animate-pulse tw-rounded-full tw-bg-jam-lav" />
  </div>
);

export const JamCardSkeleton = ({ className = "" }: { className?: string }) => (
  <div
    className={`tw-animate-pulse tw-rounded-[28px] tw-bg-jam-lav ${className}`}
  />
);
